import * as React from 'react'
import { Box } from '@mui/material'
import { Dayjs } from 'dayjs'
import FilterToolbar, {
  type FilterToolbarField,
} from '@/components/common/FilterToolbar'
import { PeriodUnit } from './filters/PeriodUnitSelect'
import { useRangeLimits } from '@/components/DatePickers/useRangeLimits'
import dayjs from 'dayjs'

type Props = {
  from: Dayjs | null
  to: Dayjs | null
  onChangeFrom: (v: Dayjs | null) => void
  onChangeTo: (v: Dayjs | null) => void
  onSearch: (range: {
    from: Dayjs | null
    to: Dayjs | null
    unit: PeriodUnit
  }) => void
  onClear?: () => void
  hasActiveFilter?: boolean
  defaultAmount?: number
  defaultUnit?: PeriodUnit
}

export default function HeaderAnalytics({
  from,
  to,
  onChangeFrom,
  onChangeTo,
  onSearch,
  onClear,
  hasActiveFilter = false,
  defaultAmount = 1,
  defaultUnit = 'weeks',
}: Props) {
  const [unit, setUnit] = React.useState<PeriodUnit>(defaultUnit)
  const [amount, setAmount] = React.useState<number | ''>(defaultAmount)

  const applyAmountUnit = (a: number | '', u: PeriodUnit) => {
    if (typeof a !== 'number' || a <= 0) return
    const now = dayjs()
    onChangeFrom(now.subtract(a, u))
    onChangeTo(now)
  }

  const handleUnitChange = (u: PeriodUnit) => {
    setUnit(u)
    applyAmountUnit(amount, u)
  }

  const handleAmountChange = (a: number | '') => {
    setAmount(a)
    applyAmountUnit(a, unit)
  }

  const handleSearch = () => {
    if (!from && !to) {
      const now = dayjs()
      const fallbackFrom =
        typeof amount === 'number'
          ? now.subtract(amount, unit)
          : now.subtract(2, 'weeks')
      onSearch({ from: fallbackFrom, to: now, unit })
      return
    }
    onSearch({ from, to, unit })
  }

  const handleClear = () => {
    setUnit(defaultUnit)
    setAmount(defaultAmount)
    onChangeFrom(null)
    onChangeTo(null)
    onClear?.()
  }

  const limits = useRangeLimits(
    { from: from?.toDate() ?? null, to: to?.toDate() ?? null },
    {}
  )

  const fields: FilterToolbarField[] = [
    {
      name: 'from-date',
      type: 'date',
      label: 'מתאריך',
      value: from?.toDate() ?? null,
      width: 'clamp(150px, 12vw, 210px)',
      disableFuture: true,
      clearable: true,
      onChange: value => onChangeFrom(value ? dayjs(value) : null),
      ...limits.from,
    },
    {
      name: 'date-separator',
      type: 'custom',
      render: <Box sx={{ color: '#BBD7FF', px: 0.25 }}>—</Box>,
    },
    {
      name: 'to-date',
      type: 'date',
      label: 'עד תאריך',
      value: to?.toDate() ?? null,
      width: 'clamp(150px, 12vw, 210px)',
      disableFuture: true,
      clearable: true,
      onChange: value => onChangeTo(value ? dayjs(value) : null),
      ...limits.to,
    },
    {
      name: 'amount',
      type: 'text',
      label: 'מספר',
      value: amount === '' ? '' : String(amount),
      placeholder: '1',
      width: 'clamp(76px, 5.2vw, 96px)',
      onChange: value => {
        if (value === '') {
          handleAmountChange('')
          return
        }
        const next = Number(value)
        if (Number.isFinite(next)) handleAmountChange(next)
      },
    },
    {
      name: 'period-unit',
      type: 'select',
      label: 'תקופה',
      value: unit,
      width: 'clamp(120px, 8vw, 150px)',
      options: [
        { value: 'weeks', label: 'שבועות' },
        { value: 'months', label: 'חודשים' },
        { value: 'years', label: 'שנים' },
      ],
      onChange: value => handleUnitChange(value as PeriodUnit),
    },
  ]

  return (
    <Box sx={{ width: '100%', direction: 'rtl' }}>
      <FilterToolbar
        fields={fields}
        onSearch={handleSearch}
        disabled={limits.hasError}
        onClear={hasActiveFilter ? handleClear : undefined}
        compact
      />
    </Box>
  )
}
